import { AgGridReact } from 'ag-grid-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getHHMMSSFromSeconds } from '../../../utils/TimeConversionFunctions';
import ColumnFilter from './ColumnFilter';
import { getSecondsFromHHMM } from '../../../utils/TimeConversionFunctions';

const PayrollReportTable = (props) => {
  const getMonthNamesData = () => {
    const data = props?.reportData?.data?.content;
    let monthNames = [];
    const personData = data?.[0];
    let modifiedPersonData = [];
    const numberOfMonths = personData?.monthlyTimingsDTOList?.length;
    for (let i = numberOfMonths - 1; i >= 0; i--) {
      const currentMonth = new Date(
        personData?.monthlyTimingsDTOList?.[i]?.startDate,
      );
      const monthName = currentMonth.toLocaleString('default', {
        month: 'long',
      });
      const year = currentMonth.toLocaleString('default', {
        year: 'numeric',
      });
      let monthTitle = monthName.concat(' ', year);
      modifiedPersonData = [...modifiedPersonData, monthTitle];
    }
    monthNames.push(modifiedPersonData);
    return monthNames;
  };
  const monthNames = getMonthNamesData();

  const compareByTime = (valueA, valueB) => {
    const secondsA = getSecondsFromHHMM(valueA);
    const secondsB = getSecondsFromHHMM(valueB);
    return secondsA - secondsB;
  };

  const getPrevMonthsHeaders = () => {
    let monthHeaders = [];
    for (let i = monthNames[0]?.length - 1; i > 0; i--) {
      let deficitTitle = 'deficitHours ' + i.toString();
      monthHeaders = [
        ...monthHeaders,
        {
          headerName: monthNames[0][i],
          children: [
            {
              headerName: 'Deficit Hours',
              field: deficitTitle,
              comparator: compareByTime,
            },
          ],
        },
      ];
    }
    return monthHeaders;
  };
  const getLatestMonthHeaders = () => {
    const monthName = monthNames[0][0];
    let monthHeaders = {
      headerName: monthName,
      children: [
        {
          headerName: 'Office Hours',
          field: 'officeHours',
          comparator: compareByTime,
        },
        {
          headerName: 'WFH Hours',
          field: 'wfhHours',
          comparator: compareByTime,
        },
        {
          headerName: 'External Hours',
          field: 'externalHours',
          comparator: compareByTime,
        },
        {
          headerName: 'Total Non-Office Hours(%)',
          field: 'totalOutsideOfficeHoursPercentage',
        },
        {
          headerName: 'Weekend Hours',
          field: 'weekendHours',
          comparator: compareByTime,
        },
        {
          headerName: 'WFH Days',
          field: 'wfhDays',
        },
        {
          headerName: 'Total Hours',
          field: 'totalHours',
          comparator: compareByTime,
        },
        {
          headerName: 'Expected Hours',
          field: 'expectedHours',
          comparator: compareByTime,
        },
        {
          headerName: 'Average Hours',
          field: 'averageHours',
        },
        {
          headerName: 'Deficit Hours',
          field: 'defecitHours',
          comparator: compareByTime,
        },
        {
          headerName: 'Auto Converted Leaves',
          field: 'autoConvertedLeaves',
        },
        {
          headerName: 'Manual Casual Leave',
          field: 'manualCasualLeaves',
        },
        {
          headerName: 'Leaves',
          field: 'leaves',
        },
      ],
    };
    return monthHeaders;
  };

  const getColumnDefs = (data) => {
    let columnDefs = [];
    columnDefs.push({
      headerName: 'Employee Details',
      children: [
        { headerName: 'Sr No', field: 'srNo' },
        { headerName: 'Employee Id', field: 'employeeCode' },
        // { headerName: 'Tenant Id', field: 'tenantId' },
        { headerName: 'Full Name', field: 'fullName' },
        { headerName: 'Office Location', field: 'officeLocation' },
        { headerName: 'Date Of Joining', field: 'dateOfJoining' },
        { headerName: 'Date Of Leaving', field: 'dateOfLeaving' },
      ],
    });
    const prevMonthsHeaders = getPrevMonthsHeaders();

    columnDefs = [...columnDefs, ...prevMonthsHeaders];
    const latestMonthHeaders = getLatestMonthHeaders();
    columnDefs.push(latestMonthHeaders);

    // Build the dynamic cumulative leave category columns from the first row's breakdown map.
    const dynamicCumulativeLeaveChildren = [];
    const breakdown = data?.[0]?.yearToDateLeaveCategoryBreakdown;
    if (breakdown && typeof breakdown === 'object') {
      Object.keys(breakdown).forEach((categoryName) => {
        dynamicCumulativeLeaveChildren.push({
          headerName: 'Cumulative ' + categoryName,
          field: 'cumLeaveBreakdown_' + categoryName,
        });
      });
    }

    columnDefs.push({
      headerName: 'Cumulative Details',
      children: [
        {
          headerName: 'Cumulative Deficit Hours',
          field: 'cumulativeDeficitHours',
          comparator: compareByTime,
        },
        {
          headerName: 'Cumulative Leaves',
          field: 'cumulativeLeaves',
        },
        // Dynamic cumulative columns — one per leave type in the DB
        ...dynamicCumulativeLeaveChildren,
        {
          headerName: 'Cumulative WFH Hours',
          field: 'cumulativeWfhHrs',
          comparator: compareByTime,
        },
        {
          headerName: 'Cumulative WFH Days Taken',
          field: 'cumulativeWfhDaysTaken',
        },
        {
          headerName: 'Cumulative WFH Days Quota',
          field: 'cumulativeWfhDaysQuota',
        },
        {
          headerName: 'Cumulative Office Hours',
          field: 'cumulativeOfficeHrs',
          comparator: compareByTime,
        },
        {
          headerName: 'Cumulative External Hours',
          field: 'cumulativeExternalHrs',
          comparator: compareByTime,
        },
        {
          headerName: 'Cumulative Manual Casual Leave',
          field: 'cumulativeManualCasualLeaves',
        },
        {
          headerName: 'Cumulative Auto Converted Leave',
          field: 'cumulativeAutoConvertedLeaves',
        },
      ],
    });
    setColumnDefs(columnDefs);
  };

  const getRowData = () => {
    const data = props?.reportData?.data?.content;
    let rowData = [];
    for (let i = 0; i < data?.length; i++) {
      const latestMonth =
        data[i]?.monthlyTimingsDTOList?.[
          data[i]?.monthlyTimingsDTOList?.length - 1
        ];
      let currentRowData = {
        srNo: i + 1 + props?.pageNo * props?.pageSize,
        employeeCode: data?.[i]?.employeeCode,
        tenantId: data[i]?.tenantId,
        fullName: data?.[i]?.fullName,
        officeLocation: latestMonth?.officeLocation || '',
        dateOfJoining: data[i]?.dateOfJoining || '',
        dateOfLeaving: data[i]?.dateOfLeaving || '',
        cumulativeDeficitHours: getHHMMSSFromSeconds(
          data[i].yearToDateDeficitHours / 1000,
        ),
        cumulativeLeaves: data[i].yearToDateLeaves,
        cumulativeWfhHrs: getHHMMSSFromSeconds(data[i].yearToDateWfhHrs / 1000),
        cumulativeWfhDaysTaken: data[i].yearToDateWfhDaysTaken,
        cumulativeWfhDaysQuota: data[i].yearToDateWfhDaysQuota,
        cumulativeOfficeHrs: getHHMMSSFromSeconds(
          data[i].yearToDateOfficeHours / 1000,
        ),
        cumulativeExternalHrs: getHHMMSSFromSeconds(
          data[i].yearToDateExternalHours / 1000,
        ),
        cumulativeManualCasualLeaves: data[i].yearToDateManualCasualLeave,
        cumulativeAutoConvertedLeaves: data[i].yearToDateAutoConvertedLeave,
      };
      // Spread the dynamic cumulative leave breakdown into the row
      const cumulativeBreakdown = data[i]?.yearToDateLeaveCategoryBreakdown;
      if (cumulativeBreakdown && typeof cumulativeBreakdown === 'object') {
        Object.entries(cumulativeBreakdown).forEach(([categoryName, count]) => {
          currentRowData['cumLeaveBreakdown_' + categoryName] = count ?? 0;
        });
      }
      const personData = data[i];
      const numberOfMonths = personData?.monthlyTimingsDTOList?.length;
      for (let j = numberOfMonths - 1; j > 0; j--) {
        let deficitTitle = 'deficitHours ' + j.toString();
        currentRowData = {
          ...currentRowData,
          [deficitTitle]: getHHMMSSFromSeconds(
            personData?.monthlyTimingsDTOList[numberOfMonths - 1 - j]
              .deficitHrs / 1000,
          ),
        };
      }
      currentRowData = {
        ...currentRowData,
        officeHours: getHHMMSSFromSeconds(
          data[i].monthlyTimingsDTOList[numberOfMonths - 1].totalOfficeHours /
            1000,
        ),
        wfhHours: getHHMMSSFromSeconds(
          data[i].monthlyTimingsDTOList[numberOfMonths - 1].totalWfhHours /
            1000,
        ),
        externalHours: getHHMMSSFromSeconds(
          data[i].monthlyTimingsDTOList[numberOfMonths - 1].totalExternalHours /
            1000,
        ),
        totalOutsideOfficeHoursPercentage:
          data?.[i]?.monthlyTimingsDTOList[numberOfMonths - 1]?.totalHours === 0
            ? data?.[i]?.monthlyTimingsDTOList[
                numberOfMonths - 1
              ]?.totalHours?.toFixed(2)
            : (
                ((data?.[i]?.monthlyTimingsDTOList[numberOfMonths - 1]
                  ?.totalExternalHours +
                  data?.[i]?.monthlyTimingsDTOList[numberOfMonths - 1]
                    ?.totalWfhHours) /
                  data?.[i].monthlyTimingsDTOList[numberOfMonths - 1]
                    ?.totalHours) *
                100
              ).toFixed(2),
        weekendHours: getHHMMSSFromSeconds(
          data?.[i]?.monthlyTimingsDTOList[numberOfMonths - 1].weekendHours /
            1000,
        ),
        wfhDays:
          data[i]?.monthlyTimingsDTOList[numberOfMonths - 1]?.workFromHomeDays,
        totalHours: getHHMMSSFromSeconds(
          data[i].monthlyTimingsDTOList[numberOfMonths - 1].totalHours / 1000,
        ),
        expectedHours: getHHMMSSFromSeconds(
          data[i].monthlyTimingsDTOList[numberOfMonths - 1].expectedHours /
            1000,
        ),
        averageHours: getHHMMSSFromSeconds(
          data[i].monthlyTimingsDTOList[numberOfMonths - 1].totalHours / 1000/((data[i].monthlyTimingsDTOList[numberOfMonths - 1].expectedHours /
            3600000)/9),
        ),
        defecitHours: getHHMMSSFromSeconds(
          data[i].monthlyTimingsDTOList[numberOfMonths - 1].deficitHrs / 1000,
        ),
        autoConvertedLeaves:
          data?.[i]?.monthlyTimingsDTOList[numberOfMonths - 1]
            ?.autoConvertedLeaves,
        manualCasualLeaves:
          data?.[i]?.monthlyTimingsDTOList[numberOfMonths - 1]
            ?.manualCasualLeaves,
        leaves: data?.[i]?.monthlyTimingsDTOList[numberOfMonths - 1]?.leaves,
      };
      rowData.push(currentRowData);
    }
    setRowData(rowData);
  };
  const gridRef = useRef(); // Optional - for accessing Grid's API
  const [rowData, setRowData] = useState(); // Set rowData to Array of Objects, one Object per Row

  // DefaultColDef sets props common to all Columns
  const defaultColDef = useMemo(() => ({
    sortable: true,
  }));

  // Example load data from server
  useEffect(() => {
    getRowData();
    getColumnDefs(props?.reportData?.data?.content);
  }, [props?.reportData]);

  const [columnDefs, setColumnDefs] = useState();
  const [gridApi, setGridApi] = useState(null);
  const [gridParams, setGridParams] = useState();

  const onGridReady = (params) => {
    setGridApi(params?.api);
    setGridParams(params);
  };

  // const onSortChanged = (event) => {
  //   const sortModel = event.api.getSortModel();
  //   props.updateSortModal(sortModel) // Store the sort model in state
  // };
  const onSortChanged = useCallback(() => {
    const value = gridParams?.columnApi
      .getColumnState()
      .find((s) => s?.sort != null);
    if (value) {
      props?.setSortModel([value]);
    } else {
      props?.setSortModel([]);
    }
  }, [gridParams, props?.setSortModel]);
  return (
    <>
      <div className="ag-theme-alpine" style={{ height: 540 }}>
        <AgGridReact
          ref={gridRef} // Ref for accessing Grid's API
          rowData={rowData} // Row Data for Rows
          columnDefs={columnDefs} // Column Defs for Columns
          defaultColDef={defaultColDef} // Default Column Properties
          animateRows={true} // Optional - set to 'true' to have rows animate when sorted
          rowSelection="multiple" // Options - allows click selection of rows
          suppressExcelExport={true}
          suppressCellFocus={true}
          onGridReady={onGridReady}
          onSortChanged={onSortChanged}
        />
      </div>
    </>
  );
};
export default PayrollReportTable;
